import { c as _c } from "react/compiler-runtime";
import { type RefObject, useLayoutEffect, useState } from 'react';
type SlotRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};
type MeasuredSlot = {
  rect: SlotRect | null;
  /** The slot the rect was measured for; it lags `slotKey` for the commit that swaps slots. */
  slotKey: string | null;
};

/**
 * Where `slot` sits inside `root`, kept current as either resizes. Empty while `slotKey` is null;
 * when the key changes to another slot, the last rect holds until the new slot is measured, so
 * whatever follows the rect stays mounted through the swap.
 */
const useSlotRect = (slotRef, rootRef, slotKey) => {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      rect: null,
      slotKey: null
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [measured, setMeasured] = useState(t0);
  let t1;
  let t2;
  if ($[1] !== rootRef || $[2] !== slotKey || $[3] !== slotRef) {
    t1 = () => {
      const slot = slotRef.current;
      const root = rootRef.current;
      if (slotKey === null) {
        setMeasured(_temp);
        return;
      }
      if (!slot || !root) {
        return;
      }
      const measure = () => {
        const next = measureSlot(slot, root);
        setMeasured(prev_0 => prev_0.slotKey === slotKey && sameRect(prev_0.rect, next) ? prev_0 : {
          rect: next,
          slotKey
        });
      };
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(slot);
      observer.observe(root);
      return () => observer.disconnect();
    };
    t2 = [slotRef, rootRef, slotKey];
    $[1] = rootRef;
    $[2] = slotKey;
    $[3] = slotRef;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useLayoutEffect(t1, t2);
  return measured;
};

// Offsets rather than getBoundingClientRect: a transform mid-animation on the slot or an ancestor
// must not move the frame that follows it.
const measureSlot = (slot: HTMLElement, root: HTMLElement): SlotRect => {
  let left = 0;
  let top = 0;
  let el: HTMLElement | null = slot;
  while (el && el !== root) {
    const parent = el.offsetParent as HTMLElement | null;
    left += el.offsetLeft + (parent?.clientLeft ?? 0);
    top += el.offsetTop + (parent?.clientTop ?? 0);
    el = parent;
  }
  return {
    left,
    top,
    width: slot.offsetWidth,
    height: slot.offsetHeight
  };
};
const sameRect = (a: SlotRect | null, b: SlotRect): boolean => a !== null && a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height;
export type { SlotRect };
export default useSlotRect;
function _temp(prev) {
  return prev.slotKey === null ? prev : {
    rect: null,
    slotKey: null
  };
}